// The routed app. `main.tsx` mounts this — it is the only entry, and there is no
// slice-1 demo entry to switch away from. (A stale comment here used to claim
// otherwise; verified 2026-08-15 against `main.tsx`, which renders `<App />`.)
//
// FROZEN after PRD 18 Wave 0. Every route the product ships is registered below,
// including screens that are still stubs under `routes/_stubs/`. A Wave 2 screen
// agent fills the route file it owns; it does not edit this file. Reachability is
// asserted in both directions by `frontend/tests/routes.test.ts` — a route added
// here without a path added there fails the suite, on purpose.
//
// A route file is not a route: `routes/` is a plain directory, not a file-based
// router. Export a `createRoute(...)`, import it here, list its path in the test.

import { StrictMode } from "react";
import {
  createRouter,
  RouterProvider,
} from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { rootRoute } from "./routes/__root";
import { adminRoute } from "./routes/_admin";
import { adminConsoleRoute } from "./routes/admin";
import { adminTenantRoute } from "./routes/admin.orgs.$orgSlug";
import { adminSupportRoute } from "./routes/admin.support";
import { shellRoute } from "./routes/_shell";
import { docsRoute } from "./routes/docs.$docId";
import { previewRoute } from "./routes/preview.$docId";
import { sourcesRoute } from "./routes/sources";
import { sourcesAddRoute } from "./routes/sources.add";
import { sourcesDetailRoute } from "./routes/sources.$id";
import { corpusSettingsRoute } from "./routes/corpora.$corpusId.settings";
import { reviewRoute } from "./routes/docs.$docId.review";
import { equationsReviewRoute } from "./routes/docs.$docId.equations";
import { corpusMergesRoute } from "./routes/corpora.$corpusId.merges";
import { dashboardRoute } from "./routes/dashboard.$orgSlug";
import { signinRoute } from "./routes/signin";
import { authLinkSentRoute } from "./routes/auth.link-sent";
import { authLinkExpiredRoute } from "./routes/auth.link-expired";
import { authInvitedRoute } from "./routes/auth.invited";
import { authCallbackRoute } from "./routes/auth.callback";
import { noAccessRoute } from "./routes/no-access";
import { trustRoute } from "./routes/trust";
import { trustProvenanceRoute } from "./routes/trust.provenance";
import { analyticsRoute } from "./routes/dashboard.$orgSlug.analytics";
import { analyticsUsageRoute } from "./routes/dashboard.$orgSlug.analytics.usage";
import { analyticsEngagementRoute } from "./routes/dashboard.$orgSlug.analytics.engagement";
import { analyticsCouplingRoute } from "./routes/dashboard.$orgSlug.analytics.coupling";
import { termDetailRoute } from "./routes/dashboard.$orgSlug.terms.$termId";
import { documentsRoute } from "./routes/dashboard.$orgSlug.documents";
import { vocabularyRoute } from "./routes/dashboard.$orgSlug.vocabulary";
import { installRoute } from "./routes/dashboard.$orgSlug.install";
import { settingsRoute } from "./routes/dashboard.$orgSlug.settings";
import { billingRoute } from "./routes/dashboard.$orgSlug.billing";
import { billingSuccessRoute } from "./routes/dashboard.$orgSlug.billing.success";
import { billingCancelledRoute } from "./routes/dashboard.$orgSlug.billing.cancelled";
import { supportRoute } from "./routes/dashboard.$orgSlug.support";
import { supportSentRoute } from "./routes/dashboard.$orgSlug.support.sent";
import "./tokens/tokens.css";

// Every shell-bearing screen hangs off `shellRoute`, a PATHLESS layout route:
// it contributes no URL segment, so each child keeps the exact `fullPath` it
// had when they were all siblings, and one `AppShell` survives every
// navigation between them (US-152).
const shelled = shellRoute.addChildren([
  reviewRoute,
  dashboardRoute,
  // S3 · analytics.
  analyticsRoute,
  analyticsUsageRoute,
  analyticsEngagementRoute,
  analyticsCouplingRoute,
  termDetailRoute,
  // S4 · content.
  documentsRoute,
  vocabularyRoute,
  // S5 · setup.
  installRoute,
  settingsRoute,
  // S6 · commerce.
  billingRoute,
  billingSuccessRoute,
  billingCancelledRoute,
  // S7 · support.
  supportRoute,
  supportSentRoute,
]);

// Exported so `screens/stateScreens.test.tsx` can mount the real tree over a
// memory history — a state screen that renders only under a hand-built tree has
// not been shown to render at its URL.
// The operator console. Its own pathless layout route, because `_shell` is
// org-scoped end to end and this surface names no org.
const adminShelled = adminRoute.addChildren([
  adminConsoleRoute,
  adminTenantRoute,
  adminSupportRoute,
]);

export const routeTree = rootRoute.addChildren([
  shelled,
  adminShelled,
  // Reading + review surfaces (pre-PRD 18) — the blueprint-dark chassis.
  docsRoute,
  previewRoute,
  sourcesRoute,
  sourcesAddRoute,
  sourcesDetailRoute,
  corpusSettingsRoute,
  equationsReviewRoute,
  corpusMergesRoute,
  // S1 · auth — pre-login, no shell.
  signinRoute,
  authLinkSentRoute,
  authLinkExpiredRoute,
  authInvitedRoute,
  authCallbackRoute,
  // S2 · shell states — decides its own chrome, see the route file.
  noAccessRoute,
  // PRD 16 C2 — the data-collection ledger, on its own surface per D2.
  trustRoute,
  // PRD 16 C1 — the provenance trail, same surface. Unlike C2 it reads
  // tenant data, so its endpoint is org-gated and the screen handles 401.
  trustProvenanceRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1 },
  },
});

export function App() {
  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>
  );
}
